// Resolves the active org's CRM deep-link URL with optional per-location
// override. Wraps `useMergedOrgToolLinks` and returns a stable
// `resolveCrmLinkUrl(lead?)` callback the GHL sender threads into its
// success-toast `action`.
//
// Per-location resolution: callers can ask for a specific lead's
// `origin_location_id` OR rely on the workspace `locationId` we close
// over here. Today the workspace location is used (matches how
// `useGhlSender` selects its payload location), but the callback
// signature accepts a lead so future surfaces can override.
//
// Slice B3 of the v1.0 Lead Control MVP polish pass — see
// `docs/v1.0-remaining-temp.md` "Daily Lead Control MVP" and
// `docs/planning/lead_control_mvp_polish.md`.

import { useCallback, useMemo } from "react";
import { useMergedOrgToolLinks } from "./useMergedOrgToolLinks";

const CRM_TOOL_ID = "crm";

export type CrmLinkLead = {
  /** Falls back to the workspace location when null/undefined. */
  origin_location_id?: string | null;
};

/**
 * Returns a stable `() => string | null` callback. Prefers the
 * lead-specific per-location override when present, then the workspace
 * per-location override, then the org base URL. Returns `null` when no
 * URL is configured so callers can omit the toast action cleanly.
 */
export function useResolveCrmLinkUrl(orgId: string | null, locationId: string | null) {
  const { toolLinks } = useMergedOrgToolLinks(orgId);

  const crm = useMemo(() => toolLinks.find((t) => t.id === CRM_TOOL_ID) ?? null, [toolLinks]);

  return useCallback(
    (lead?: CrmLinkLead): string | null => {
      if (!crm) return null;
      const perLocation = crm.perLocation ?? {};
      // Prefer the lead's own origin location if the caller supplied one;
      // fall back to the workspace's active location; fall back to the
      // org-level base URL last.
      const leadLocId = lead?.origin_location_id ?? null;
      const fromLead = leadLocId ? perLocation[leadLocId]?.trim() : "";
      if (fromLead) return fromLead;
      const fromWorkspace = locationId ? perLocation[locationId]?.trim() : "";
      if (fromWorkspace) return fromWorkspace;
      const fromBase = (crm.baseUrl ?? "").trim();
      return fromBase ? fromBase : null;
    },
    [crm, locationId],
  );
}

/**
 * Pure picker exposed for unit tests + non-React callers. Mirrors the
 * priority order the React hook uses internally.
 */
export function pickCrmLinkUrl(
  crm: { baseUrl?: string | null; perLocation?: Record<string, string> } | null | undefined,
  options: { workspaceLocationId?: string | null; leadOriginLocationId?: string | null } = {},
): string | null {
  if (!crm) return null;
  const perLocation = crm.perLocation ?? {};
  const leadLocId = options.leadOriginLocationId ?? null;
  const fromLead = leadLocId ? (perLocation[leadLocId] ?? "").trim() : "";
  if (fromLead) return fromLead;
  const wsLocId = options.workspaceLocationId ?? null;
  const fromWorkspace = wsLocId ? (perLocation[wsLocId] ?? "").trim() : "";
  if (fromWorkspace) return fromWorkspace;
  const fromBase = (crm.baseUrl ?? "").trim();
  return fromBase ? fromBase : null;
}
